import { useRequest } from "@certn/api";
import { Link } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { PropsWithChildren } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useUser } from "../hooks/useUser";
import { useOptionalClientPortalBootstrap } from "../providers/clientPortalBootstrapContext";
import { exitProxySessionWithToast } from "../utils/proxyExit";

export function ProxyProvider({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const { details } = useUser();
  const request = useRequest();
  const { caseManagementClientProxy } = useFlags();
  const { details: userDetails } = useUser();
  const bootstrap = useOptionalClientPortalBootstrap();

  const stopProxy = async () => {
    const purgeProtectedCache =
      bootstrap?.purgeProtectedCache ??
      (async () => {
        return undefined;
      });

    const result = await exitProxySessionWithToast(
      request,
      t,
      purgeProtectedCache
    );

    if (!result.ok) {
      return;
    }
  };

  return (
    <>
      {details?.client_account &&
        userDetails?.is_proxy_user &&
        caseManagementClientProxy && (
          <div className={styles.container}>
            <div className={styles.inner}>
              <Link
                testID="return-to-cm-link"
                linkStyle="monochrome"
                textStyle="global/body-secondary"
                textColor="text-inverse"
                onPress={stopProxy}
              >
                <Trans>Return to Case Management</Trans>
              </Link>
            </div>
          </div>
        )}
      {children}
    </>
  );
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    border: "var(--space-2) solid var(--background-accent-purple-strong)",
    position: "fixed",
    width: "calc(100vw - var(--space-4))",
    height: "calc(100vh - var(--space-4))",
    zIndex: 100,
    pointerEvents: "none",
  }),
  inner: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: "370px",
    width: "100vw",
    backgroundColor: "var(--background-accent-purple-strong)",
    borderBottomLeftRadius: "var(--border-radius-3)",
    borderBottomRightRadius: "var(--border-radius-3)",
    pointerEvents: "all",
    padding: "var(--space-1) 0",
  }),
};
